/**
 * Command Palette — Linear/Vercel-style fuzzy-search modal.
 *
 * Открывается Cmd+K (mac) / Ctrl+K (others). Ищет по:
 *   - страницам приложения (через PAGES);
 *   - route'ам пользователя (по имени, ID, паре);
 *   - символам из state.symbols.
 *
 * Управление: ↑↓ навигация, Enter — открыть, Esc — закрыть, обычный ввод —
 * фильтр. Mounted глобально в AppShell.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Rocket, Coins, Repeat, GitBranch, KeyRound,
  BellRing, Activity, Settings as SettingsIcon, Command, Layers, ShieldCheck,
  Sigma,
} from 'lucide-react';
import clsx from 'clsx';
import { useAppStore } from '../store/useStore';
import { canSeeXStocks } from '../utils/xstocksAccess';
import { canSeeOptions } from '../utils/optionsAccess';
import { isAdmin } from '../utils/adminAccess';
import { sharedGet } from '../utils/sharedGet';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

// Lucide-icons имеют свой LucideProps тип (size: string|number), несовместимый
// с узким ComponentType<{size:number}>. Используем общий FC any-icon.
type IconType = React.FC<{ size?: number | string; className?: string }>;

interface Item {
  id: string;
  label: string;
  hint?: string;
  // Идентификатор группы; подпись берётся из словаря (groups.<id>).
  group: 'pages' | 'routes' | 'symbols';
  icon: IconType;
  go: () => void;
  score?: number;
}

const PAGES: Array<{ label: string; hint?: string; group: 'pages'; icon: IconType; to: string }> = [
  { label: i18n.t('commandPalette:pages.terminal'), to: '/',         group: 'pages', icon: Rocket,        hint: i18n.t('commandPalette:pages.terminalHint') },
  { label: i18n.t('commandPalette:pages.scanner'),  to: '/scanner',  group: 'pages', icon: Coins },
  { label: i18n.t('commandPalette:pages.auto'),     to: '/auto',     group: 'pages', icon: Repeat },
  { label: i18n.t('commandPalette:pages.alerts'),   to: '/alerts',   group: 'pages', icon: BellRing },
  { label: i18n.t('commandPalette:pages.funding'),  to: '/funding',  group: 'pages', icon: Activity },
  { label: i18n.t('commandPalette:pages.xstocks'),  to: '/xstocks',  group: 'pages', icon: Layers },
  { label: i18n.t('commandPalette:pages.options'),  to: '/options',  group: 'pages', icon: Sigma },
  { label: i18n.t('commandPalette:pages.apiKeys'),  to: '/api-keys', group: 'pages', icon: KeyRound },
  { label: i18n.t('commandPalette:pages.profile'),  to: '/profile',  group: 'pages', icon: SettingsIcon },
  { label: i18n.t('commandPalette:pages.admin'),    to: '/admin',    group: 'pages', icon: ShieldCheck,   hint: i18n.t('commandPalette:pages.adminHint') },
];

/** Simple fuzzy: substring + word-start bonus. Чем выше score — тем релевантнее. */
function score(q: string, text: string): number {
  if (!q) return 1;
  const Q = q.toLowerCase();
  const T = text.toLowerCase();
  if (T === Q) return 1000;
  if (T.startsWith(Q)) return 500;
  // ищем все символы Q в T по порядку
  let i = 0;
  for (const ch of T) {
    if (ch === Q[i]) i++;
    if (i === Q.length) break;
  }
  if (i !== Q.length) return 0;
  // бонус если match плотный (Q.length / T.length close to 1)
  return Math.floor(100 * (Q.length / Math.max(T.length, 1)));
}

export const CommandPalette: React.FC = () => {
  const { t } = useTranslation('commandPalette');
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const routes  = useAppStore(s => s.routes);
  // Symbols загружаем лениво при первом открытии палитры — иначе тащить
  // 8 бирж × ~300 символов в каждый mount AppShell ради hotkey'a жирно.
  const [symbols, setSymbols] = useState<string[]>([]);
  useEffect(() => {
    if (!open || symbols.length) return;
    // sharedGet: терминал этот список уже просил — берём его ответ, а не шлём
    // третий такой же запрос в очередь к бэкенду.
    sharedGet('/api/symbols', 60_000).then(d => {
      // Без сырых синонимов бирж (1000PEPE, KPEPE…) — как в выборе тикера.
      const list = (d?.canonical || d?.symbols || d) as string[];
      if (Array.isArray(list)) setSymbols(list);
    }).catch(() => {/* silent */});
  }, [open, symbols.length]);

  // Глобальный hotkey
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.code === 'KeyK') {
        e.preventDefault();
        setOpen(o => !o);
        setQ('');
        setIdx(0);
      } else if (e.code === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Auto-focus при открытии
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  const items: Item[] = useMemo(() => {
    const out: Item[] = [];
    // pages
    for (const p of PAGES) {
      if (p.to === '/xstocks' && !canSeeXStocks(useAppStore.getState().user)) continue;
      if (p.to === '/options' && !canSeeOptions(useAppStore.getState().user)) continue;
      if (p.to === '/admin' && !isAdmin(useAppStore.getState().user)) continue;
      const s = Math.max(score(q, p.label), score(q, p.hint || ''));
      if (s > 0) out.push({ id: `page-${p.to}`, label: p.label, hint: p.hint, group: 'pages', icon: p.icon, go: () => nav(p.to), score: s });
    }
    // routes
    const rArr = Array.isArray(routes) ? routes : [];
    for (const r of rArr.slice(0, 100)) {
      const name = r.name || t('routeFallback', { id: r.id });
      const pair = `${r.left_symbol || ''} ${r.left_exchange}↔${r.right_exchange}`;
      const s = Math.max(score(q, name), score(q, pair), score(q, String(r.id)));
      if (s > 0) out.push({
        id: `route-${r.id}`, label: name, hint: pair, group: 'routes', icon: GitBranch,
        go: () => nav(`/routes/${r.id}`), score: s + 5, /* slight boost */
      });
    }
    // symbols
    const symArr = Array.isArray(symbols) ? symbols : [];
    for (const sym of symArr.slice(0, 500)) {
      const s = score(q, sym);
      if (s > 0) out.push({
        id: `sym-${sym}`, label: sym, hint: t('openInTerminal'), group: 'symbols', icon: Rocket,
        go: () => nav(`/?symbol=${sym}`), score: s,
      });
    }
    out.sort((a, b) => (b.score || 0) - (a.score || 0));
    return out.slice(0, 30);
  }, [q, routes, symbols, nav, t]);

  // Группировка для отображения
  const groups = useMemo(() => {
    const g: Record<string, Item[]> = {};
    for (const it of items) (g[it.group] ||= []).push(it);
    return g;
  }, [items]);

  // Reset selection при фильтрации
  useEffect(() => { setIdx(0); }, [q]);

  // Navigation ↑↓ Enter
  const onKey = (e: React.KeyboardEvent) => {
    if (e.code === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(items.length - 1, i + 1)); }
    if (e.code === 'ArrowUp')   { e.preventDefault(); setIdx(i => Math.max(0, i - 1)); }
    if (e.code === 'Enter') {
      e.preventDefault();
      const it = items[idx];
      if (it) { it.go(); setOpen(false); }
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-background/80 pt-[10vh] backdrop-blur-sm"
         onClick={() => setOpen(false)}>
      <div className="w-full max-w-lg mx-4 rounded-xl border border-border bg-popover shadow-2xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
          <Search size={14} className="text-muted-foreground"/>
          <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKey}
                 placeholder={t('placeholder')}
                 className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-muted-foreground"/>
          <span className="hidden sm:inline-flex items-center gap-1 text-[10px] text-muted-foreground font-mono">
            <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted">esc</kbd>
          </span>
        </div>

        <div className="max-h-[60vh] overflow-y-auto py-1">
          {items.length === 0 && (
            <div className="px-4 py-8 text-center text-xs text-muted-foreground">
              {t('empty', { q })}
            </div>
          )}
          {Object.entries(groups).map(([gName, gItems]) => (
            <div key={gName} className="mb-1">
              <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-muted-foreground/70">{t(`groups.${gName}`)}</div>
              {gItems.map(it => {
                const globalIdx = items.indexOf(it);
                const active = globalIdx === idx;
                const Icon = it.icon;
                return (
                  <button key={it.id} onClick={() => { it.go(); setOpen(false); }}
                    onMouseEnter={() => setIdx(globalIdx)}
                    className={clsx(
                      'w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left',
                      active ? 'bg-accent/60 text-foreground' : 'text-foreground/90 hover:bg-accent/30',
                    )}>
                    <Icon size={14} className="shrink-0 text-muted-foreground"/>
                    <span className="font-medium truncate">{it.label}</span>
                    {it.hint && <span className="text-[11px] text-muted-foreground truncate ml-1">· {it.hint}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="border-t border-border px-3 py-1.5 text-[10px] text-muted-foreground flex items-center gap-3 font-mono">
          <span className="inline-flex items-center gap-1"><kbd className="px-1 rounded bg-muted">↑↓</kbd> {t('keys.select')}</span>
          <span className="inline-flex items-center gap-1"><kbd className="px-1 rounded bg-muted">↵</kbd> {t('keys.open')}</span>
          <span className="ml-auto inline-flex items-center gap-1"><Command size={9}/>{t('keys.toggle')}</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
